import { type ReactNode, useEffect } from 'react';
import Logo from '@avanta/shared/assets/avanta-simple.svg';
import { useMessageVirtualizer } from '@avanta/shared/hooks/Chat/useMessageVirtualizer';
import { useReadReceipts } from '@avanta/shared/hooks/Chat/useReadReceipts';
import { useRealtimeMessages } from '@avanta/shared/hooks/Chat/useRealtimeMessages';
import { useScrollToLastMessage } from '@avanta/shared/hooks/Chat/useScrollToLastMessage';
import { useTypingSubscription } from '@avanta/shared/hooks/Chat/useTypingSubscription';
import { useUnreadCounts } from '@avanta/shared/hooks/Chat/useUnreadCounts';
import {
  useActiveConversation,
  useChannelIds,
  useConversationsStore,
} from '@avanta/shared/stores/chat/conversations';
import { useChatStore } from '@avanta/shared/stores/chat/index';
import { useMessagesStore } from '@avanta/shared/stores/chat/messages';
import { type OptimisticMessage } from '@avanta/shared/types/chat';
import { cn } from '@avanta/shared/utils/cn';
import { usePresenceSubscription } from '../../../hooks/Chat/usePresenceSubscription';
import { ConversationSkeleton } from '../ConversationSkeleton';
import { Message } from '../Message';
import { PinnedMessage } from '../PinnedMessage';
import { TypingIndicator } from '../TypingIndicator';

const EMPTY_MESSAGES: OptimisticMessage[] = [];

const DEFAULT_EMPTY_STATE = (
  <img src={Logo} alt="Avanta Mynd Logo" className="h-12 grayscale opacity-50" />
);

interface ConversationThreadProps {
  header: ReactNode;
  welcome: ReactNode;
  input: ReactNode;
  emptyState?: ReactNode;
  className?: string;
}

export const ConversationThread = ({
  header,
  welcome,
  input,
  emptyState = DEFAULT_EMPTY_STATE,
  className,
}: ConversationThreadProps): React.ReactElement | null => {
  const chat = useChatStore(state => state.chat);
  const activeChannelId = useConversationsStore(state => state.activeChannelId);
  const isLoadingConversation = useConversationsStore(state => state.isLoadingConversation);
  const isCreatingConversation = useConversationsStore(state => state.isCreatingConversation);
  const activeConversation = useActiveConversation();

  const messages = useMessagesStore(
    state => (activeChannelId ? state.messages.get(activeChannelId) : undefined) ?? EMPTY_MESSAGES
  );
  const isLoadingMessages = useMessagesStore(state =>
    activeChannelId ? (state.isLoading.get(activeChannelId) ?? false) : false
  );
  const loadMessages = useMessagesStore(state => state.loadMessages);
  const resetUnreadCount = useConversationsStore(state => state.resetUnreadCount);

  const channelIds = useChannelIds();

  useRealtimeMessages({ channelId: activeChannelId, chat });
  useUnreadCounts({ channelIds, activeChannelId, chat });
  useTypingSubscription({
    activeChannelId,
    chat,
    otherUserId: activeConversation?.otherUser.id,
  });
  usePresenceSubscription({
    activeChannelId,
    chat,
    otherUserId: activeConversation?.otherUser.id,
  });
  useReadReceipts({ activeChannelId, chat });

  useEffect(() => {
    if (!activeChannelId || !chat) return;
    const alreadyLoaded = useMessagesStore.getState().messages.has(activeChannelId);
    if (!alreadyLoaded) {
      void loadMessages(activeChannelId);
    }
  }, [activeChannelId, chat, loadMessages]);

  const lastMessageTimetoken = messages[messages.length - 1]?.timetoken;

  useEffect(() => {
    if (!activeChannelId || !chat) return;
    resetUnreadCount(activeChannelId);
  }, [activeChannelId, chat, messages.length, lastMessageTimetoken, resetUnreadCount]);

  const { scrollContainerRef, virtualizer, shouldVirtualize } = useMessageVirtualizer(messages);

  useScrollToLastMessage({
    scrollContainerRef,
    virtualizer,
    messages,
    channelId: activeChannelId,
    shouldVirtualize,
  });

  const isInitialLoad =
    isCreatingConversation ||
    ((isLoadingConversation || isLoadingMessages) && messages.length === 0);

  if (isInitialLoad) {
    return <ConversationSkeleton />;
  }

  return (
    <div className={cn('flex-1 flex flex-col border border-plum-200 overflow-hidden', className)}>
      {activeChannelId ? (
        <>
          {header}
          <PinnedMessage />
          <div
            ref={scrollContainerRef}
            className="flex flex-col am-lg:gap-6 gap-4 px-6 pb-4 am-lg:pt-8 pt-4 flex-1 overflow-y-auto relative"
          >
            {welcome}
            {shouldVirtualize ? (
              <div
                className="relative h-(--virtual-height)"
                style={
                  { '--virtual-height': `${virtualizer.getTotalSize()}px` } as React.CSSProperties
                }
              >
                {virtualizer.getVirtualItems().map(virtualItem => {
                  const message = messages[virtualItem.index];
                  return (
                    <div
                      key={message.clientId}
                      data-index={virtualItem.index}
                      ref={virtualizer.measureElement}
                      className="absolute top-0 left-0 w-full translate-y-(--virtual-offset)"
                      style={
                        { '--virtual-offset': `${virtualItem.start}px` } as React.CSSProperties
                      }
                    >
                      <Message message={message} />
                    </div>
                  );
                })}
              </div>
            ) : (
              messages.map(message => <Message key={message.clientId} message={message} />)
            )}
            <TypingIndicator />
          </div>
          {input}
        </>
      ) : (
        <div className="flex justify-center items-center flex-1">{emptyState}</div>
      )}
    </div>
  );
};
